import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface MentionProviderProps {
  baseUrl: string;
  getAccessToken: () => Promise<string>;
  children: ReactNode;
  /**
   * The entity (tenant) UUID used to scope API calls.
   *
   * Required by "smart" components like <MentionChip /> and <MentionHoverCard />
   * so that they can self-hydrate user details for previously-mentioned users
   * without the caller having to prop-drill entityId everywhere.
   *
   * The input components (<MentionInputConfigurable />, <MentionInputComposable />)
   * still accept their own `entityId` prop which takes precedence — callers
   * embedding multiple inputs under a single provider can override per-input.
   */
  entityId?: string;
  /**
   * Debounce delay in milliseconds before triggering user search.
   * Waits for this delay after the last keystroke before making an API call.
   *
   * @default 150
   * @recommended 150-300ms for optimal UX
   *
   * - Lower values (50-100ms): More responsive but more API calls
   * - Higher values (300-500ms): Fewer API calls but less responsive
   */
  debounceMs?: number;
  /**
   * Minimum query length before triggering search.
   *
   * NOTE: This MUST match the AC User API v2 requirement for the 'name' parameter.
   * The AC API requires minimum 3 characters for name-based search and returns
   * 400 Bad Request if less than 3 non-wildcard characters are provided.
   *
   * @default 3
   * @readonly This value is dictated by Access Control API limitations
   * @see https://controlpanel.ogintegration.us/api/v2/users (name parameter docs)
   */
  minQueryLength?: number;
  /**
   * The name of the current user who is creating mentions.
   *
   * Used CLIENT-SIDE only — to construct the `mentionText` value in the
   * `POST /mentions` payload (e.g. "You have been mentioned by {name}").
   *
   * The SERVER-SIDE Beacon notification title is independent of this prop:
   * the mention service resolves the caller's full name from
   * `IdentityService.getUser(callerUserId)` (see
   * `packages/services/src/mention.service.ts` -> `callerFullName`), so
   * misconfiguring or omitting this prop will not affect the title shown in
   * the recipient's in-app notification.
   *
   * @optional If not provided, `mentionText` falls back to the mentioned
   *           user's full name.
   */
  currentUserName?: string;
}

interface MentionContextValue {
  baseUrl: string;
  getAccessToken: () => Promise<string>;
  entityId: string | undefined;
  debounceMs: number;
  minQueryLength: number;
  currentUserName: string | undefined;
}

const MentionContext = createContext<MentionContextValue | null>(null);

export function MentionProvider({
  baseUrl,
  getAccessToken,
  entityId,
  debounceMs = 150,
  minQueryLength = 3,
  currentUserName,
  children,
}: MentionProviderProps) {
  const value = useMemo(
    () => ({ baseUrl, getAccessToken, entityId, debounceMs, minQueryLength, currentUserName }),
    [baseUrl, getAccessToken, entityId, debounceMs, minQueryLength, currentUserName],
  );
  return <MentionContext.Provider value={value}>{children}</MentionContext.Provider>;
}

export function useMentionContext(): MentionContextValue {
  const ctx = useContext(MentionContext);
  if (!ctx) throw new Error('useMentionContext must be used within a <MentionProvider>');
  return ctx;
}

/**
 * Non-throwing variant of `useMentionContext`. Returns `null` when no
 * <MentionProvider /> is present. Used by smart components (MentionChip,
 * MentionHoverCard) so they can render safely in isolation — e.g. in
 * Storybook or unit tests — as long as all display data is passed as props.
 */
export function useMentionContextOptional(): MentionContextValue | null {
  return useContext(MentionContext);
}
